import { apiClient } from '../utils/apiMonitoring';

class UserSettingsApi {
  constructor() {
    this.baseUrl = '/api/users';
  }

  // Helper method to handle API responses
  async handleResponse(response) {
    if (!response.ok) {
      // Try to get error details from response
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (jsonError) {
        // If we can't parse JSON, use the status text
        errorMessage = response.statusText || errorMessage;
      }
      
      const error = new Error(errorMessage);
      error.response = response;
      throw error;
    }
    
    const data = await response.json();
    return data;
  }

  // Security Settings
  async getSecuritySettings() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/security-settings`);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching security settings:', error);
      throw error;
    }
  }

  async updateSecuritySettings(settings) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/security-settings`, settings);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error updating security settings:', error);
      throw error;
    }
  }

  // Notification Settings
  async getNotificationSettings() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/notification-settings`);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      throw error;
    }
  }

  async updateNotificationSettings(settings) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/notification-settings`, settings);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error updating notification settings:', error);
      throw error;
    }
  }

  // Session Management
  async getSessionTimeout() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/session-timeout`);
      const data = await this.handleResponse(response);
      return data.session_timeout_minutes;
    } catch (error) {
      console.error('Error fetching session timeout:', error);
      return 30; // Default fallback
    }
  }

  async getUserSessions() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/sessions`);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching user sessions:', error);
      throw error;
    }
  }

  async logoutSession(sessionId) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/sessions/${sessionId}/logout`);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error logging out session:', error);
      throw error;
    }
  }

  // Two-Factor Authentication
  async setupTwoFactor(method, phoneNumber = null) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/2fa/setup`, {
        method,
        phone_number: phoneNumber
      });
      
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error setting up 2FA:', error);
      throw error;
    }
  }

  async verifyTwoFactor(code, method) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/2fa/verify`, {
        code,
        method
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      throw error;
    }
  }

  async disableTwoFactor() {
    try {
      const response = await apiClient.delete(`${this.baseUrl}/2fa/disable`);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      throw error;
    }
  }

  // Login Notifications
  async sendLoginNotification(sessionData) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/login-notification`, sessionData);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error sending login notification:', error);
      // Don't throw error for login notifications as they're not critical
    }
  }

  // Device Detection Utilities
  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    
    // Simple device detection
    let device = 'Desktop';
    if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
      device = 'Mobile';
    } else if (/Tablet|iPad/.test(userAgent)) {
      device = 'Tablet';
    }

    // Browser detection
    let browser = 'Unknown';
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';

    return {
      device,
      browser,
      platform,
      userAgent: userAgent.substring(0, 200) // Limit length
    };
  }

  // Password Management
  async updatePassword(currentPassword, newPassword) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: newPassword
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  }

  // Location detection (simplified - in production you'd use a proper geolocation service)
  async getLocationInfo() {
    try {
      // This is a simplified version - in production you'd use a proper IP geolocation service
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      return {
        city: data.city || 'Unknown',
        country: data.country_name || 'Unknown',
        region: data.region || 'Unknown'
      };
    } catch (error) {
      console.error('Error getting location info:', error);
      return {
        city: 'Unknown',
        country: 'Unknown',
        region: 'Unknown'
      };
    }
  }

}

export const userSettingsApi = new UserSettingsApi();
